import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import CartIcon from '../shared/CartIcon';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleCartClick = () => {
    window.location.hash = '#cart';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      // Element exists on current page - scroll to it
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      // Update the URL hash without triggering a page reload
      window.history.replaceState(null, '', href);
    } else {
      // Element doesn't exist - we're probably in cart view, redirect to home with section
      window.location.hash = '#home';
      // Small delay to allow navigation to complete, then scroll to section
      setTimeout(() => {
        const homeElement = document.getElementById(targetId);
        if (homeElement) {
          homeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
        window.history.replaceState(null, '', href);
      }, 100);
    }
  };

  const navItems = [
    { href: '#preview-gallery', label: t('nav.gallery') },
    { href: '#how-we-work', label: t('nav.about') },
    { href: '#pricing', label: t('nav.pricing') },
    { href: '#faq', label: 'FAQ' },
    { href: '#contact', label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-dark-primary/95 backdrop-blur-md border-b border-dark-border/30' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-6">
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = '#home';
            }}
            className="flex items-center space-x-2 cursor-pointer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="font-playfair text-2xl font-light text-cream tracking-wider">
              TamerinTECH
            </span>
            <span className="text-gold/80 text-sm font-light tracking-widest uppercase">
              AI Art
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-12">
            {navItems.map((item, index) => (
              <motion.a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(item.href, e)}
                className="relative text-cream-dark hover:text-cream font-light text-sm tracking-wider uppercase transition-colors duration-300 group"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-px bg-gold group-hover:w-full transition-all duration-300"></span>
              </motion.a>
            ))}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex items-center space-x-6"
            >
              <CartIcon onClick={handleCartClick} />
              <LanguageSwitcher />
            </motion.div>
          </nav>

          {/* Mobile Menu Button and Cart */}
          <div className="md:hidden flex items-center space-x-4">
            <CartIcon onClick={handleCartClick} />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-cream hover:text-gold transition-colors"
              aria-label="Toggle menu"
            >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.svg
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </motion.svg>
              ) : (
                <motion.svg
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </motion.svg>
              )}
            </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden"
            >
              <div className="py-4 space-y-4 border-t border-dark-border/30">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    className="block text-cream-dark hover:text-cream font-light text-sm tracking-wider uppercase transition-colors duration-300 py-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    onClick={(e) => {
                      e.preventDefault();
                      console.log('Mobile nav clicked:', item.href); // Debug log
                      const targetId = item.href.replace('#', '');
                      const element = document.getElementById(targetId);

                      setIsMobileMenuOpen(false);

                      if (element) {
                        // Element exists on current page - scroll to it
                        setTimeout(() => {
                          element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                          window.history.replaceState(null, '', item.href);
                        }, 300); // Wait for menu to close
                      } else {
                        // Element doesn't exist - we're probably in cart view, redirect to home with section
                        window.location.hash = '#home';
                        // Small delay to allow navigation to complete, then scroll to section
                        setTimeout(() => {
                          const homeElement = document.getElementById(targetId);
                          if (homeElement) {
                            homeElement.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                          window.history.replaceState(null, '', item.href);
                        }, 400);
                      }
                    }}
                  >
                    {item.label}
                  </motion.a>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="pt-4 flex justify-center"
                >
                  <LanguageSwitcher />
                </motion.div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;